import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
    title, 
    description, 
    type = 'article', 
    image = '%PUBLIC_URL%/images/simple-medical-ai-logo.svg', 
    canonicalUrl,
    publishedTime,
    modifiedTime,
    authorName = 'PCRAssist',
    jsonLd = null
}) => {
    const siteUrl = process.env.REACT_APP_SITE_URL || 'https://pcrassist.com';
    const location = useLocation();
    
    // Ensure index.html redirects to root URL in canonical
    const path = location.pathname === '/index.html' ? '/' : location.pathname;
    
    // Default JSON-LD for articles if none provided
    const defaultJsonLd = type === 'article' ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: description,
        image: image,
        author: {
            '@type': 'Organization',
            name: authorName,
            url: siteUrl
        },
        publisher: {
            '@type': 'Organization',
            name: 'PCRAssist',
            url: siteUrl,
            logo: {
                '@type': 'ImageObject',
                url: `${siteUrl}/images/simple-medical-ai-logo.svg`
            }
        },
        url: `${siteUrl}${canonicalUrl || path}`,
        datePublished: publishedTime,
        dateModified: modifiedTime || publishedTime
    } : null;

    return (
        <Helmet>
            {/* Standard metadata */}
            <title>{title} | PCRAssist</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={`${siteUrl}${canonicalUrl || path}`} />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={`${siteUrl}${path}`} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
            
            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={`${siteUrl}${path}`} />
            <meta property="twitter:title" content={title} />
            <meta property="twitter:description" content={description} />
            <meta property="twitter:image" content={image} />

            {/* JSON-LD structured data */}
            {(jsonLd || defaultJsonLd) && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd || defaultJsonLd)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO; 